<template>
  <div class="settings">
    <VPBreadcrumbs :elements="[{ text: 'Venues', to: parentRoute }, { text: venueName }]" />

    <Form
      v-if="venue"
      :form-id="formId"
      :initial-values="venue"
    >
      <VenueFields />

      <FormActionButtons
        :show-save="isFormDirty()"
        :show-cancel="isFormDirty()"
        show-delete
        @save="handleSave"
        @delete="triggerDeleteConfirmation"
        @cancel="onCancel"
      />
    </Form>

    <VPModal
      v-model:show="showDeleteConfirmation"
      title="Confirm Venue Deletion"
      confirm-label="Delete"
      close-label="Cancel"
      size="sm"
      @confirm="confirmDelete"
      @close="closeModal"
    >
      <p class="text-center text-vp-base-900">
        Are you sure you want to delete the venue '{{ venueName }}'?
      </p>
    </VPModal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import VenueFields from './VenueFields.vue';
import { Form, useFormsStoreComposable } from 'vuniform';
import { useVenueStore } from '@/stores/venue';
import type { SaveVenueMutationInput, VenueAttributes } from '../../generated/graphql';
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import FormActionButtons from '@/components/form/FormActionButtons.vue';
import VPBreadcrumbs from '@/components/VPBreadcrumbs.vue';
import VPModal from '@/components/form/VPModal.vue';

const venueStore = useVenueStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const { formId, formsStore, formGetValues, isFormDirty } = useFormsStoreComposable('edit-venue-form');
const venue = computed(() => venueStore.venues.find(({ id }) => id == route.params.venueId));
const venueName = computed(() => venue.value?.name || '');
const parentRoute = { name: 'VenueIndex' };

const showDeleteConfirmation = ref(false);

const triggerDeleteConfirmation = () => {
  showDeleteConfirmation.value = true;
};

const confirmDelete = async () => {
  await venueStore.deleteVenue(venue.value!.id);
  toast.success(`Venue '${venueName.value}' has been deleted!`);
  showDeleteConfirmation.value = false;
  router.push(parentRoute);
};

const closeModal = () => {
  showDeleteConfirmation.value = false;
};
const handleSave = async () => {
  const values = formGetValues<VenueAttributes>();
  delete (values as { logo: any })['logo'];
  // add a check if values.rooms id is empty, or _destroy is true, and remove it
  values.rooms = values.rooms?.filter(room => room.id || !room._destroy);
  const payload: SaveVenueMutationInput = {
    clientMutationId: formId,
    venue: values,
  };
  const { result: newVenue, errors } = await venueStore.saveVenue(payload);
  if (errors) {
    formsStore.SET_ERRORS({ requesterId: formId, errors });
    return;
  }

  if (!newVenue) {
    return;
  }

  toast.success(`Venue '${newVenue.name}' updated successfully!`);
  router.push(parentRoute);
};

const onCancel = () => formsStore.RESET_FIELD_VALUES(formId);
</script>
